// Free geocoding utilities using multiple free services

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 
 * @param {number} lon1 
 * @param {number} lat2 
 * @param {number} lon2 
 * @returns {number} Distance in miles
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Check if string looks like a UK postcode
 * @param {string} input 
 * @returns {boolean}
 */
export const isUKPostcode = (input) => {
  const postcodeRegex = /^[A-Z]{1,2}[0-9R][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;
  return postcodeRegex.test(input.replace(/\s/g, ''));
};

/**
 * Geocode UK postcode using PostcodesIO (free, no API key)
 * @param {string} postcode 
 * @returns {Promise<{lat: number, lng: number, location: string}>}
 */
export const geocodeUKPostcode = async (postcode) => {
  try {
    const cleanPostcode = postcode.replace(/\s/g, '');
    const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`);
    
    if (!response.ok) {
      throw new Error('Postcode not found');
    }
    
    const data = await response.json();
    
    if (data.status === 200 && data.result) {
      return {
        lat: data.result.latitude,
        lng: data.result.longitude,
        location: `${data.result.admin_ward}, ${data.result.admin_district}`,
        postcode: data.result.postcode
      };
    }
    
    throw new Error('Invalid postcode response');
  } catch (error) {
    console.error('UK postcode geocoding failed:', error);
    throw error;
  }
};

/**
 * Geocode address/place using Nominatim (OpenStreetMap - free, no API key)
 * @param {string} query 
 * @returns {Promise<{lat: number, lng: number, location: string}>}
 */
export const geocodeLocation = async (query) => {
  try {
    // Add UK bias to search
    const searchQuery = `${query}, United Kingdom`;
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&countrycodes=gb&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'ServiceCentersMap/1.0' // Required by Nominatim
        }
      }
    );
    
    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }
    
    const data = await response.json();
    
    if (data.length > 0) {
      const result = data[0];
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        location: result.display_name,
        address: result.address
      };
    }
    
    throw new Error('Location not found');
  } catch (error) {
    console.error('Location geocoding failed:', error);
    throw error;
  }
};

/**
 * Main geocoding function that tries multiple services
 * @param {string} input 
 * @returns {Promise<{lat: number, lng: number, location: string}>}
 */
export const geocodeInput = async (input) => {
  const trimmedInput = input.trim();
  
  if (!trimmedInput) {
    throw new Error('Please enter a postcode or location');
  }
  
  // Try UK postcode API first if it looks like a postcode
  if (isUKPostcode(trimmedInput)) {
    try {
      return await geocodeUKPostcode(trimmedInput);
    } catch (error) {
      console.log('UK postcode API failed, trying general geocoding...');
      // Fall through to general geocoding
    }
  }
  
  // Try general location geocoding
  try {
    return await geocodeLocation(trimmedInput);
  } catch (error) {
    throw new Error(`Could not find location: ${trimmedInput}`);
  }
};

/**
 * Sort locations by distance from a point
 * @param {Array} locations 
 * @param {number} userLat 
 * @param {number} userLng 
 * @returns {Array} Locations with distance property, sorted by distance
 */
export const sortLocationsByDistance = (locations, userLat, userLng) => {
  return locations
    .map(location => ({
      ...location,
      distance: calculateDistance(
        userLat, 
        userLng, 
        location.coordinates[0], 
        location.coordinates[1]
      )
    }))
    .sort((a, b) => a.distance - b.distance);
};

/**
 * Format distance for display
 * @param {number} distance 
 * @returns {string}
 */
export const formatDistance = (distance) => {
  if (distance < 1) {
    return `${(distance * 5280).toFixed(0)} ft`;
  } else if (distance < 10) {
    return `${distance.toFixed(1)} miles`;
  } else {
    return `${Math.round(distance)} miles`;
  }
};

/**
 * Get the nearest locations within a radius
 * @param {Array} locations 
 * @param {number} userLat 
 * @param {number} userLng 
 * @param {number} maxRadius Maximum distance in miles (default 50)
 * @returns {Array} Nearby locations sorted by distance
 */
export const getNearbyLocations = (locations, userLat, userLng, maxRadius = 50) => {
  return sortLocationsByDistance(locations, userLat, userLng)
    .filter(location => location.distance <= maxRadius);
};